import React, { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash/isEqual';
import { WatchQueryFetchPolicy } from '@apollo/client';
import { useRouter } from 'next/compat/router';
import { getPromptCreatePageUrl } from 'src/services/pagesUrl/getPromptCreatePageUrl';
import { useTerritoryPromptList } from './hooks/useTerritoryPromptList';
import { useCollapsedSection } from './hooks/useCollapsedSection';
import { TERRITORY_EXPLORE_POSTS_PAGE_SIZE } from './constants';
import { TerritoryLivePromptActiveFilter } from '../TerritoryFilter/constants';
import { getFilteredLivePrompts } from './helpers/getFilteredLivePrompts';
import { TerritoryPromptsContent } from './TerritoryPromptsContent';
import { PromptsSortingDropdownKey } from '../PromptsListSection/constants';
import { useModal } from '../Modal/useModal';
import { PromptSettingsLiveStatus, PromptTypePicker } from '../Prompt/constants';
import { CreatePromptForm } from '../CreatePrompt/CreatePromptForm';
import { CloseConfirmationModal } from '../ConfirmationModal/CloseConfirmationModal';
import { TerritoryVersionType } from '../../layouts/TerritoryPageLayout/TerritoryPageLayout';
import { useAnalytics } from '../AnalyticsProvider/AnalyticsProvider';
import { getFeatureFlag } from '../FeatureFlags/getFeatureFlag';

type TTerritoryPromptsContainerProps = {
  territoryId: string;
  sectionTitle: string;
  sortBy: PromptsSortingDropdownKey;
  initialPageSize?: number;
  where?: object;
  fetchPolicy?: WatchQueryFetchPolicy;
  livePromptStatus?: TerritoryLivePromptActiveFilter;
  isPinningEnabled: boolean;
  canManagePinnedArticles: boolean;
  hideSortingDropdown?: boolean;
  isAuthLoading?: boolean;
  territorySlug: string;
  territoryVersion: TerritoryVersionType;
  hideSeeAllButton?: boolean;
  seeAllUrl?: string;
};

export const TerritoryPromptsContainer: FC<TTerritoryPromptsContainerProps> = ({
  territoryId,
  sectionTitle,
  sortBy,
  initialPageSize = TERRITORY_EXPLORE_POSTS_PAGE_SIZE,
  where,
  fetchPolicy,
  livePromptStatus,
  isPinningEnabled,
  canManagePinnedArticles,
  hideSortingDropdown = false,
  isAuthLoading,
  territorySlug,
  territoryVersion,
  hideSeeAllButton = false,
  seeAllUrl,
}) => {
  const [currentSortBy, setCurrentSortBy] = useState<PromptsSortingDropdownKey>(sortBy);
  const { prompts, onLoadMore, getSortedPromptsByTerritoryId, hasMorePromptsToLoad, isLoading, promptsCount } =
    useTerritoryPromptList({ territoryId, initialPageSize });
  const { sendEvent } = useAnalytics();
  const router = useRouter();

  const {
    isCollapsedSection,
    renderedArticles: renderedPrompts,
    updateCollapsedSection,
  } = useCollapsedSection(prompts, hasMorePromptsToLoad, initialPageSize);

  const renderedPromptsWithLiveStatus = livePromptStatus
    ? getFilteredLivePrompts(renderedPrompts, livePromptStatus)
    : renderedPrompts;

  const totalPromptsCounts = livePromptStatus ? (renderedPromptsWithLiveStatus?.length as number) : promptsCount;

  const showLoadMoreButton = totalPromptsCounts > initialPageSize;

  const [promptType, setPromptType] = useState<PromptTypePicker>();
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const onClosePromptModalCallback = () => {
    setPromptType(PromptTypePicker.STANDARD);
    onClosePromptModal();
  };
  const {
    openModal: openPromptModal,
    closeModal: closePromptModal,
    ModalComponent: PromptModal,
  } = useModal({
    closeModalCallback: onClosePromptModalCallback,
  });
  const {
    openModal: openPromptConfirmationModal,
    closeModal: closePromptConfirmationModal,
    ModalComponent: PromptConfirmationModalComponent,
  } = useModal();

  const onOpenPromptModal = () => {
    if (getFeatureFlag('promptV2')) {
      router?.push(getPromptCreatePageUrl(territorySlug));
    } else {
      setPromptType(PromptTypePicker.STANDARD);
      openPromptModal();
    }
  };

  const defaultValues = {
    title: '',
    description: '',
    startDate: new Date(),
    endDate: new Date(),
  };

  const useFormReturn = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
  });

  const onClosePromptModal = () => {
    const {
      getValues,
      formState: { defaultValues, isSubmitting },
    } = useFormReturn;

    if (!isSubmitting && !isEqual(getValues(), defaultValues)) {
      openPromptConfirmationModal();
    }
  };

  const onLoadMoreWithToggle = async () => {
    sendEvent('territory_prompts_load_more_click');
    setIsLoadingMore(true);
    await onLoadMore();
    updateCollapsedSection();
    setIsLoadingMore(false);
  };

  const onSortByChange = (sortBy: PromptsSortingDropdownKey) => {
    setCurrentSortBy(sortBy);
    getSortedPromptsByTerritoryId({
      variables: {
        skip: 0,
        sortBy,
        take: initialPageSize,
        ...(where && { where }),
      },
      ...(fetchPolicy && { fetchPolicy }),
    });
  };

  useEffect(() => {
    getSortedPromptsByTerritoryId({
      notifyOnNetworkStatusChange: true,
      variables: {
        sortBy: currentSortBy,
        ...(where && { where }),
      },
      ...(fetchPolicy && { fetchPolicy }),
    });
  }, [sortBy, where, fetchPolicy]);

  return (
    <>
      <TerritoryPromptsContent
        territoryId={territoryId}
        canManagePinnedArticles={canManagePinnedArticles}
        hasMorePromptsToLoad={hasMorePromptsToLoad}
        isCollapsedSection={isCollapsedSection}
        isLoading={isLoading && !isLoadingMore}
        isPinningEnabled={isPinningEnabled}
        onLoadMoreWithToggle={onLoadMoreWithToggle}
        renderedPromptsWithLiveStatus={renderedPromptsWithLiveStatus}
        sectionTitle={sectionTitle}
        showLoadMoreButton={showLoadMoreButton}
        onSortByChange={onSortByChange}
        hideSortingDropdown={hideSortingDropdown}
        sortBy={currentSortBy}
        openPromptModal={onOpenPromptModal}
        isAuthLoading={isAuthLoading}
        territorySlug={territorySlug}
        territoryVersion={territoryVersion}
        hideSeeAllButton={hideSeeAllButton}
        seeAllUrl={seeAllUrl}
      />

      <PromptModal title="Prompts" containerClassName="sm:!max-h-[90%] !max-w-[640px] pt-16 sm:pt-6">
        <CreatePromptForm
          promptType={promptType}
          promptSettingsLiveStatus={
            promptType === PromptTypePicker.LIVE ? PromptSettingsLiveStatus.UPCOMING : undefined
          }
          territoryId={territoryId}
          closeModal={closePromptModal}
          useFormReturn={useFormReturn}
          setPromptType={setPromptType}
        />
      </PromptModal>
      <CloseConfirmationModal
        ModalComponent={PromptConfirmationModalComponent}
        closeConfirmationModal={closePromptConfirmationModal}
        closeModal={closePromptModal}
        openModal={openPromptModal}
      />
    </>
  );
};
